import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Calendar, Clock, User, ArrowLeft, Loader2, Share2, BookOpen } from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback content for demo posts
const FALLBACK_CONTENT = {
  "understanding-tdsr-bahamas": `
## What is TDSR?

Total Debt Service Ratio (TDSR) is a financial metric that lenders use to assess your ability to manage and repay debt. It represents the percentage of your gross monthly income that goes toward paying all your debt obligations.

## How to Calculate Your TDSR

The formula is simple:

**TDSR = (Total Monthly Debt Payments ÷ Gross Monthly Income) × 100**

Your total monthly debt payments include:
- Mortgage or rent payments
- Car loan payments
- Credit card minimum payments
- Personal loan payments
- Student loan payments
- Any other recurring debt obligations

### Example Calculation

Let's say your gross monthly income is BSD 5,000 and your monthly debt payments are:
- Mortgage: BSD 1,200
- Car loan: BSD 400
- Credit cards: BSD 200

**Total Debt = BSD 1,800**
**TDSR = (1,800 ÷ 5,000) × 100 = 36%**

## What Banks Look For

Most Bahamian banks prefer a TDSR below **40-45%**. Here's a general guide:

| TDSR Range | Assessment |
|------------|------------|
| Below 30% | Excellent - Strong approval chances |
| 30-40% | Good - Most banks will approve |
| 40-45% | Acceptable - May face some restrictions |
| Above 45% | Risky - May be declined or require co-signer |

## Tips to Improve Your TDSR

1. **Pay down existing debt** - Focus on high-interest debt first
2. **Increase your income** - Consider a side job or ask for a raise
3. **Avoid new debt** - Don't take on new loans before applying
4. **Consolidate debt** - May lower your monthly payments

## The Bottom Line

Understanding your TDSR before applying for a loan can save you time and help you prepare for success. Use Financify's free pre-qualification tool to see where you stand with Bahamian banks.
  `,
  "first-time-home-buyer-bahamas": `
## Getting Started with Your First Home

Buying your first home in The Bahamas is an exciting milestone. This guide will walk you through everything you need to know about the mortgage process.

## Understanding Mortgage Types

### Fixed-Rate Mortgages
- Interest rate stays the same throughout the loan term
- Predictable monthly payments
- Most common choice for first-time buyers

### Variable-Rate Mortgages
- Interest rate can change based on market conditions
- May start lower than fixed rates
- Monthly payments can increase or decrease

## Down Payment Requirements

Most Bahamian banks require:
- **Minimum 5-10%** for first-time buyers
- **10-20%** for subsequent purchases
- **Lower rates** often available with 20%+ down

## Documents You'll Need

1. Valid government-issued ID
2. Proof of income (job letter, pay stubs)
3. Bank statements (last 6 months)
4. Tax clearance certificate
5. Property appraisal

## The Application Process

1. **Get Pre-Qualified** - Understand what you can afford
2. **Find Your Home** - Work with a real estate agent
3. **Submit Application** - Provide all required documents
4. **Appraisal** - Bank assesses property value
5. **Approval** - Receive your mortgage offer
6. **Closing** - Sign papers and get your keys!

## Tips for Success

- Keep your credit clean in the months before applying
- Save for closing costs (2-5% of purchase price)
- Don't make major purchases before closing
- Get pre-approved before house hunting
  `,
  "improve-loan-approval-chances": `
## 5 Ways to Boost Your Loan Approval Odds

Getting approved for a loan isn't just about having a high income. Banks look at the complete picture. Here are five proven strategies to strengthen your application.

## 1. Check Your Credit Report First

Before applying, review your CRIF report for:
- Errors or outdated information
- Outstanding debts you forgot about
- Accounts that need attention

**Tip:** Fix any errors at least 30 days before applying.

## 2. Lower Your Debt-to-Income Ratio

Banks prefer applicants with a TDSR below 40%. To improve yours:
- Pay off credit card balances
- Avoid opening new credit accounts
- Consider debt consolidation

## 3. Stabilize Your Employment

Lenders love stability. Strengthen your profile by:
- Staying with your current employer (if possible)
- Having at least 2 years of employment history
- Getting a job letter that highlights your tenure

## 4. Save for a Larger Down Payment

A bigger down payment shows commitment and reduces the bank's risk:
- Aim for at least 10-20% if possible
- Shows you can manage money
- May qualify you for better rates

## 5. Choose the Right Loan Amount

Don't over-borrow! Request what you actually need:
- Calculate realistic monthly payments
- Leave room in your budget for emergencies
- Consider future expenses

## Bonus: Use Pre-Qualification Tools

Tools like Financify help you understand where you stand before applying, so you can address weaknesses in your application.
  `
};

export default function BlogPostPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/posts/${slug}`);
        setPost(response.data);
      } catch (err) {
        // Try fallback content
        if (FALLBACK_CONTENT[slug]) {
          setPost({
            title: slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '),
            slug: slug,
            content: FALLBACK_CONTENT[slug],
            excerpt: "Read this comprehensive guide...",
            category: "Credit Education",
            author: "Financify Team",
            created_at: new Date().toISOString()
          });
        } else {
          setError("Blog post not found");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'long', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const estimateReadTime = (content) => {
    if (!content) return "3 min";
    const words = content.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min`;
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: url
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  // Simple markdown-like rendering
  const renderContent = (content) => {
    if (!content) return null;
    
    return content.split('\n').map((line, index) => {
      // Headers
      if (line.startsWith('## ')) {
        return <h2 key={index} className="text-2xl font-bold text-[#2D3748] mt-8 mb-4">{line.slice(3)}</h2>;
      }
      if (line.startsWith('### ')) {
        return <h3 key={index} className="text-xl font-semibold text-[#2D3748] mt-6 mb-3">{line.slice(4)}</h3>;
      }
      
      // Bold text
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={index} className="text-gray-700 mb-4 leading-relaxed">
            {parts.map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part)}
          </p>
        );
      }
      
      // List items
      if (line.startsWith('- ')) {
        return (
          <li key={index} className="text-gray-700 ml-6 mb-2 list-disc">
            {line.slice(2)}
          </li>
        );
      }
      if (line.match(/^\d+\. /)) {
        return (
          <li key={index} className="text-gray-700 ml-6 mb-2 list-decimal">
            {line.replace(/^\d+\. /, '')}
          </li>
        );
      }
      
      // Table rows (simple)
      if (line.startsWith('|')) {
        const cells = line.split('|').filter(c => c.trim());
        if (cells.some(c => c.includes('---'))) return null; // Skip separator row
        return (
          <div key={index} className="grid grid-cols-2 gap-4 py-2 border-b border-gray-100">
            {cells.map((cell, i) => (
              <span key={i} className={`text-sm ${i === 0 ? 'font-medium text-[#2D3748]' : 'text-gray-600'}`}>
                {cell.trim()}
              </span>
            ))}
          </div>
        );
      }
      
      // Empty lines
      if (!line.trim()) {
        return <div key={index} className="h-2" />;
      }
      
      // Regular paragraphs
      return <p key={index} className="text-gray-700 mb-4 leading-relaxed">{line}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#52BFB8]" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h1 className="text-2xl font-bold text-[#2D3748] mb-2">Post Not Found</h1>
          <p className="text-gray-500 mb-6">The blog post you're looking for doesn't exist.</p>
          <Link to="/blog">
            <Button className="bg-[#52BFB8] hover:bg-[#3DA8A1]">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blog
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Back Button */}
      <div className="bg-white border-b border-gray-100">
        <div className="container-main py-4">
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-gray-600 hover:text-[#52BFB8] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Article */}
      <article className="py-12">
        <div className="container-main">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span 
                  className="text-sm font-medium px-3 py-1 rounded-full"
                  style={{ background: 'rgba(82, 191, 184, 0.15)', color: '#3DA8A1' }}
                >
                  {post.category}
                </span>
              </div>
              
              <h1 className="text-3xl md:text-4xl font-bold text-[#2D3748] mb-4">
                {post.title}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                <span className="flex items-center gap-1">
                  <User className="w-4 h-4" />
                  {post.author}
                </span>
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(post.created_at || post.published_at)}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {estimateReadTime(post.content)} read
                </span>
                <button 
                  onClick={handleShare}
                  className="flex items-center gap-1 hover:text-[#52BFB8] transition-colors ml-auto"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              </div>
            </header>

            {/* Featured Image */}
            {post.featured_image && (
              <div className="mb-8 rounded-2xl overflow-hidden">
                <img 
                  src={post.featured_image} 
                  alt={post.title}
                  className="w-full h-auto"
                />
              </div>
            )}

            {/* Content */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 md:p-12">
              {post.excerpt && (
                <p className="text-lg text-gray-600 mb-8 pb-8 border-b border-gray-100 italic">
                  {post.excerpt}
                </p>
              )}
              
              <div className="prose prose-lg max-w-none">
                {renderContent(post.content)}
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 bg-gradient-to-r from-[#1a2f4a] to-[#2d4a5e] rounded-2xl p-8 text-center text-white">
              <h3 className="text-2xl font-bold mb-3">Ready to Check Your Eligibility?</h3>
              <p className="text-white/80 mb-6">
                Use our free pre-qualification tool to see which Bahamian banks match your profile.
              </p>
              <Link to="/qualify">
                <Button size="lg" className="bg-[#52BFB8] hover:bg-[#3DA8A1] text-white">
                  Get Pre-Qualified Now
                </Button>
              </Link>
            </div>

            {/* Back to Blog */}
            <div className="mt-8 text-center">
              <Link 
                to="/blog" 
                className="inline-flex items-center gap-2 text-[#52BFB8] hover:text-[#3DA8A1] font-medium"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to all articles
              </Link>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}
